import { useCallback, useEffect, useMemo, useState } from "react"
import type { AppShell } from "../App"
import {
  loadRadar,
  markDelivered,
  upsertOverlay,
  type RadarProject,
  type RadarResponse,
} from "../lib/api"
import { Button } from "../components/ui/button"
import { Badge } from "../components/ui/badge"
import { cn } from "../lib/utils"

/** Fim da semana corrente — o foco expira sozinho e obriga a decidir à segunda. */
function endOfWeek(): string {
  const now = new Date()
  const daysToFriday = (5 - now.getDay() + 7) % 7
  const friday = new Date(now)
  friday.setDate(now.getDate() + daysToFriday)
  return friday.toISOString().slice(0, 10)
}

function euros(value: number | null): string {
  if (value === null) return "—"
  return `€${Math.round(value).toLocaleString("pt-PT")}`
}

function money(project: RadarProject): string {
  if (project.proposal_value !== null) return euros(project.proposal_value)
  return project.value_bucket ? `Tamanho ${project.value_bucket}` : "—"
}

const HEALTH_LABEL: Record<RadarProject["health"], string> = {
  on_track: "On track",
  at_risk: "Atenção",
  delayed: "Em risco",
}

function GateRail({ project }: { project: RadarProject }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
      {project.gates.map((gate) => {
        const delivered = Boolean(gate.delivered_at)
        const unbilled = delivered && gate.status === "forecasted"
        const isNext = project.next_milestone?.id === gate.id
        return (
          <span
            key={gate.id}
            className={cn(
              delivered && "text-emerald-600 dark:text-emerald-400",
              unbilled && "text-amber-600 dark:text-amber-400",
              isNext && "font-semibold text-foreground",
            )}
          >
            {delivered ? "✓" : isNext ? "●" : "○"} {gate.label ?? `Marco ${gate.sequence}`}
            {unbilled && " · por faturar"}
          </span>
        )
      })}
    </div>
  )
}

function FocusCard({
  project,
  shell,
  onChanged,
}: {
  project: RadarProject
  shell: AppShell
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const next = project.next_milestone

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      onChanged()
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível guardar", description: (err as Error).message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <article className="flex flex-col rounded-md border bg-card shadow-sm">
      <div className="border-b px-4 pb-3 pt-4">
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-primary">
            {project.reference}
          </span>
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {project.client_name ?? "—"}
          </span>
          <Badge variant={project.health === "on_track" ? "secondary" : "destructive"}>
            {HEALTH_LABEL[project.health]}
          </Badge>
        </div>
        <h3 className="text-base font-semibold leading-tight">{project.project_name}</h3>
        <div className="mt-2 flex flex-wrap gap-4 font-mono text-xs text-muted-foreground">
          <span>{money(project)}</span>
          <span>{project.logged_hours.toFixed(1)} h</span>
          <span className="font-sans">{project.owner_name ?? "sem owner"}</span>
        </div>
      </div>

      {project.overlay?.blocker_text && (
        <div className="flex gap-2 border-b bg-destructive/10 px-4 py-2.5 text-sm">
          <span className="font-mono text-[10px] uppercase tracking-wider text-destructive">
            Bloqueio
          </span>
          <span>{project.overlay.blocker_text}</span>
        </div>
      )}

      {next ? (
        <div className="border-b bg-primary/5 px-4 py-3">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-widest text-primary">
            A fechar
          </span>
          <div className="mt-1 flex flex-wrap items-baseline gap-2">
            <b className="text-[15px]">{next.label ?? `Marco ${next.sequence}`}</b>
            <span className="font-mono text-xs text-muted-foreground">
              {next.expected_at ? next.expected_at.slice(0, 10) : "sem data definida"}
            </span>
            <span className="ml-auto font-mono text-sm font-semibold text-primary">
              {euros(next.amount)}
            </span>
          </div>
        </div>
      ) : (
        <div className="border-b px-4 py-3 text-sm text-muted-foreground">
          Sem marcos por entregar.
        </div>
      )}

      <div className="px-4 py-3">
        <div className="mb-2 flex items-baseline gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          <span>{project.open_issues_count} issues em aberto</span>
          {project.closed_this_week > 0 && (
            <span className="ml-auto normal-case text-emerald-600 dark:text-emerald-400">
              {project.closed_this_week} fechadas esta semana
            </span>
          )}
        </div>
        {project.issues.map((issue) => (
          <div
            key={issue.reference}
            className="grid grid-cols-[auto_1fr_auto] items-baseline gap-2 border-b border-dotted py-1 text-sm last:border-0"
          >
            <span className="font-mono text-[10px] text-muted-foreground">{issue.reference}</span>
            <span className="truncate">{issue.title}</span>
            <span
              className={cn(
                "whitespace-nowrap font-mono text-[10px] text-muted-foreground",
                issue.over && "text-destructive",
              )}
            >
              {issue.assignee?.split(" ")[0] ?? "—"} · {issue.spent_hours}/{issue.estimated_hours}h
            </span>
          </div>
        ))}
      </div>

      <GateRail project={project} />

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t bg-muted/40 px-4 py-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-primary">
          ◆ em foco até {project.overlay?.focus_pinned_until?.slice(0, 10) ?? "—"}
        </span>
        <div className="flex gap-2">
          {project.contract_id && (
            <Button
              size="sm"
              variant="ghost"
              title="Abrir o contrato no CRM (fases, marcos, valores)"
              onClick={() =>
                shell.bridge.navigateCrm({ href: `/contracts/${project.contract_id}` })
              }
            >
              Contrato →
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() =>
              run(() =>
                upsertOverlay(shell.client, {
                  project_reference: project.reference,
                  focus_pinned_until: null,
                  mark_reviewed: true,
                }),
              )
            }
          >
            Tirar do foco
          </Button>
          {next && project.contract_id && (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => run(() => markDelivered(shell.client, project.contract_id!, next.id))}
            >
              Marcar entrega
            </Button>
          )}
        </div>
      </div>
    </article>
  )
}

export function RadarPage({ shell }: { shell: AppShell }) {
  const [data, setData] = useState<RadarResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<string | null>(null)

  const refresh = useCallback(() => {
    loadRadar(shell.client)
      .then((res) => {
        setData(res)
        setError(null)
      })
      .catch((err) => setError((err as Error).message))
  }, [shell.client])

  useEffect(refresh, [refresh])

  const focusCap = 6
  const groups = useMemo(() => {
    const projects = data?.projects ?? []
    const focus = projects.filter((p) => p.is_focus)
    const focusRefs = new Set(focus.map((p) => p.reference))
    return {
      focus,
      active: projects.filter((p) => p.bucket === "active" && !focusRefs.has(p.reference)),
      waiting: projects.filter((p) => p.bucket === "waiting"),
      dormant: projects.filter((p) => p.bucket === "dormant"),
      candidates: projects
        .filter((p) => !focusRefs.has(p.reference) && p.candidate_signals.length > 0)
        .sort((a, b) => b.candidate_signals.length - a.candidate_signals.length)
        .slice(0, 4),
    }
  }, [data])

  const promote = async (project: RadarProject) => {
    if (groups.focus.length >= focusCap) {
      setPending(
        `O foco está cheio — ${focusCap} é o tecto. Tira um projeto antes de promover ${project.project_name}.`,
      )
      return
    }
    try {
      await upsertOverlay(shell.client, {
        project_reference: project.reference,
        focus_pinned_until: endOfWeek(),
        mark_reviewed: true,
      })
      setPending(null)
      refresh()
    } catch (err) {
      shell.bridge.toast({ variant: "error", title: "Não foi possível guardar", description: (err as Error).message })
    }
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-lg font-semibold text-destructive">Não foi possível ler o Radar</h1>
        <p className="mt-1 text-sm text-muted-foreground">{error}</p>
      </div>
    )
  }
  if (!data) return <div className="p-6 text-sm text-muted-foreground">A carregar projetos…</div>

  const { totals } = data

  return (
    <div className="mx-auto max-w-[1200px] px-6 pb-16">
      <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-md border bg-border sm:grid-cols-5">
        {[
          { value: String(totals.active), label: "Ativos", sub: `worklog ≤ ${data.thresholds.activeDays} dias` },
          { value: String(groups.focus.length), label: "Focus now", sub: `tecto: ${focusCap}` },
          { value: String(totals.at_risk), label: "Em risco", sub: "marco vencido" },
          {
            value: euros(totals.waiting_amount),
            label: "À espera de terceiros",
            sub: `${totals.waiting} projetos parados`,
          },
          {
            value: euros(totals.delivered_unbilled_amount),
            label: "Entregue por faturar",
            sub: "marcos desbloqueados",
          },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-card px-4 py-3">
            <div className="font-mono text-2xl font-semibold leading-none">{kpi.value}</div>
            <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {kpi.label}
            </div>
            <div className="text-[11px] text-muted-foreground">{kpi.sub}</div>
          </div>
        ))}
      </div>

      <SectionHead
        title="Focus Now"
        eyebrow={`${groups.focus.length} de ${totals.active} · até sexta`}
        note="Até 6. Estar aqui não é estar atrasado — é onde a semana se decide."
      />
      {pending && (
        <p className="mb-3 border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm">{pending}</p>
      )}
      {groups.focus.length === 0 ? (
        <p className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
          Nada em foco. Promove um dos candidatos abaixo para arrancar a semana.
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {groups.focus.map((project) => (
            <FocusCard key={project.reference} project={project} shell={shell} onChanged={refresh} />
          ))}
        </div>
      )}

      {groups.candidates.length > 0 && (
        <>
          <SectionHead
            title="Candidatos a foco"
            eyebrow="sugestão · a decisão é tua"
            note="Levantados por sinais, não por atividade."
          />
          <div className="grid gap-px overflow-hidden rounded-md border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {groups.candidates.map((project) => (
              <div key={project.reference} className="flex flex-col gap-2 bg-card px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary">
                    {project.reference}
                  </span>
                  <b className="text-sm">{project.project_name}</b>
                </div>
                <p className="text-sm text-muted-foreground">
                  {project.candidate_signals.map((s) => s.label).join(" · ")}
                </p>
                <div className="mt-auto flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => promote(project)}>
                    Promover a foco
                  </Button>
                  {project.contract_id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        shell.bridge.navigateCrm({ href: `/contracts/${project.contract_id}` })
                      }
                    >
                      Contrato →
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <SectionHead
        title="A avançar"
        eyebrow={`${groups.active.length} projetos`}
        note="Existem, mexeram-se, não precisam da reunião."
      />
      <ProjectTable
        projects={groups.active}
        columns="active"
        shell={shell}
        onPromote={promote}
        focusFull={groups.focus.length >= focusCap}
      />

      <SectionHead
        title="À espera de terceiros"
        eyebrow={`${groups.waiting.length} projetos · ${euros(totals.waiting_amount)} no marco`}
        note="Não é ruído: cada linha é um telefonema que vale dinheiro."
      />
      <ProjectTable
        projects={groups.waiting}
        columns="waiting"
        shell={shell}
        onPromote={promote}
        focusFull={groups.focus.length >= focusCap}
      />

      {groups.dormant.length > 0 && (
        <details className="mt-4 rounded-md border border-dashed">
          <summary className="cursor-pointer px-4 py-3 text-sm text-muted-foreground">
            {groups.dormant.length} dormentes — sem trabalho há mais de {data.thresholds.dormantDays} dias
          </summary>
          <div className="px-4 pb-3">
            <ProjectTable
              projects={groups.dormant}
              columns="dormant"
              shell={shell}
              onPromote={promote}
              focusFull={groups.focus.length >= focusCap}
            />
          </div>
        </details>
      )}
    </div>
  )
}

function SectionHead({ title, eyebrow, note }: { title: string; eyebrow: string; note: string }) {
  return (
    <div className="mb-3 mt-9 flex flex-wrap items-baseline gap-3 border-b pb-2">
      <h2 className="text-base font-semibold">{title}</h2>
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {eyebrow}
      </span>
      <span className="ml-auto text-xs text-muted-foreground">{note}</span>
    </div>
  )
}

function ProjectTable({
  projects,
  columns,
  shell,
  onPromote,
  focusFull,
}: {
  projects: RadarProject[]
  columns: "active" | "waiting" | "dormant"
  shell: AppShell
  onPromote: (project: RadarProject) => void
  focusFull: boolean
}) {
  if (projects.length === 0) {
    return (
      <p className="rounded-md border border-dashed px-4 py-5 text-center text-sm text-muted-foreground">
        Nada aqui.
      </p>
    )
  }
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead>
          <tr className="border-b text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2">Projeto</th>
            <th className="px-3 py-2">Cliente</th>
            <th className="px-3 py-2">Owner</th>
            <th className="px-3 py-2">{columns === "waiting" ? "Falta" : "Fase"}</th>
            <th className="px-3 py-2 text-right">
              {columns === "waiting" ? "Parado há" : "Últ. atividade"}
            </th>
            <th className="px-3 py-2 text-right">Ações</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => (
            <tr key={project.reference} className="border-b last:border-0 hover:bg-muted/40">
              <td className="px-3 py-2">
                <span className="mr-2 font-mono text-[10px] text-muted-foreground">
                  {project.reference}
                </span>
                {project.project_name}
              </td>
              <td className="px-3 py-2 text-muted-foreground">{project.client_name ?? "—"}</td>
              <td className="px-3 py-2">{project.owner_name ?? "—"}</td>
              <td className="px-3 py-2 text-muted-foreground">
                {columns === "waiting"
                  ? project.next_milestone?.label ?? "—"
                  : project.current_phase ?? "—"}
              </td>
              <td className="px-3 py-2 text-right font-mono text-xs">
                {project.last_activity_days === null ? "—" : `${project.last_activity_days} d`}
              </td>
              <td className="px-3 py-2">
                <div className="flex items-center justify-end gap-2">
                  {/* Estar parado não é razão para não poder ir a foco — o
                      objetivo pode ser exactamente pô-lo a andar. */}
                  {!project.is_focus && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      disabled={focusFull}
                      title={focusFull ? "Foco cheio — tira um projeto primeiro" : "Promover a foco"}
                      onClick={() => onPromote(project)}
                    >
                      Promover
                    </Button>
                  )}
                  {project.contract_id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      title="Abrir o contrato no CRM (fases, marcos, valores)"
                      onClick={() =>
                        shell.bridge.navigateCrm({ href: `/contracts/${project.contract_id}` })
                      }
                    >
                      Contrato →
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
